import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  Home, 
  Clock, 
  BookOpen, 
  Trophy, 
  User, 
  LogIn, 
  LogOut,
  Moon,
  Sun,
  Palette
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';

const NavbarContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #E5E5E7;
  z-index: var(--z-fixed);
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  ${props => props.$scrolled === 'true' && `
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
  `}
  
  ${props => props.theme.isDark && `
    background: rgba(26, 32, 44, 0.8);
    border-bottom-color: #2D3748;
  `}
`;

const NavbarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  font-weight: 700;
  color: #007AFF;
  text-decoration: none;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
    text-decoration: none;
    color: #0056CC;
  }
  
  .logo-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 16px;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  color: #1D1D1F;
  text-decoration: none;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.08);
    text-decoration: none;
    transform: translateY(-1px);
  }
  
  &.active {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.1);
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%);
      width: 20px;
      height: 2px;
      background: #007AFF;
      border-radius: 1px;
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DesktopAuthLink = styled(NavLink)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
`;

const UserButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 8px;
  background: transparent;
  color: #1D1D1F;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.08);
    transform: translateY(-1px);
  }
  
  .avatar {
    width: 28px;
    height: 28px;
    border-radius: 14px;
    background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 12px;
    box-shadow: 0 1px 3px rgba(0, 122, 255, 0.3);
  }
`;

const DesktopUserButton = styled(UserButton)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const ThemeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  color: #1D1D1F;
  transition: all 0.2s ease;
  
  &:hover {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.08);
    transform: translateY(-1px);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: transparent;
  color: #1D1D1F;
  transition: all 0.2s ease;
  
  &:hover {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.08);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: var(--color-background);
  border-bottom: 1px solid var(--color-border);
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  z-index: var(--z-dropdown);
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: var(--border-radius-md);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 500;
  transition: all var(--animation-duration) ease;
  
  &:hover {
    color: var(--color-primary);
    background: rgba(102, 126, 234, 0.1);
    text-decoration: none;
  }
  
  &.active {
    color: var(--color-primary);
    background: rgba(102, 126, 234, 0.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  background: #FFFFFF;
  border: 1px solid #E5E5E7;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  padding: 8px;
  min-width: 200px;
  z-index: var(--z-dropdown);
  backdrop-filter: blur(20px);
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: transparent;
  color: #1D1D1F;
  text-align: left;
  font-weight: 500;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #007AFF;
    background: rgba(0, 122, 255, 0.08);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { currentTheme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // 监听滚动事件
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 关闭移动端菜单
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/', label: '首页', icon: Home },
    { path: '/timeline', label: '时间线', icon: Clock },
    { path: '/quiz', label: '测验', icon: BookOpen },
    { path: '/leaderboard', label: '排行榜', icon: Trophy },
  ];

  const getThemeIcon = () => {
    switch (currentTheme) {
      case 'dark':
        return Sun;
      case 'classic':
        return Palette;
      default:
        return Moon;
    }
  };

  const ThemeIcon = getThemeIcon();

  return (
    <NavbarContainer $scrolled={scrolled.toString()}>
      <NavbarContent>
        <Logo to="/">
          <div className="logo-icon">史</div>
          <span>历史小课堂</span>
        </Logo>

        <NavLinks>
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              className={location.pathname === path ? 'active' : ''}
            >
              <Icon />
              {label}
            </NavLink>
          ))}
        </NavLinks>

        <UserMenu>
          <ThemeButton onClick={toggleTheme} title="切换主题">
            <ThemeIcon />
          </ThemeButton>

          {isAuthenticated ? (
            <div style={{ position: 'relative' }}>
              <DesktopUserButton onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                <div className="avatar">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span>{user?.username}</span>
              </DesktopUserButton>

              <AnimatePresence>
                {isUserMenuOpen && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <DropdownItem onClick={() => navigate('/profile')}>
                      <User />
                      个人中心
                    </DropdownItem>
                    <DropdownItem onClick={handleLogout}>
                      <LogOut />
                      退出登录
                    </DropdownItem>
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <DesktopAuthLink to="/login">
              <LogIn />
              登录
            </DesktopAuthLink>
          )}

          <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </MobileMenuButton>
        </UserMenu>
      </NavbarContent>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map(({ path, label, icon: Icon }) => (
              <MobileNavLink
                key={path}
                to={path}
                className={location.pathname === path ? 'active' : ''}
              >
                <Icon />
                {label}
              </MobileNavLink>
            ))}

            {isAuthenticated ? (
              <>
                <MobileNavLink to="/profile">
                  <User />
                  个人中心
                </MobileNavLink>
                <DropdownItem onClick={handleLogout}>
                  <LogOut />
                  退出登录
                </DropdownItem>
              </>
            ) : (
              <MobileNavLink to="/login">
                <LogIn />
                登录
              </MobileNavLink>
            )}
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavbarContainer>
  );
};

export default Navbar;
